import React, { useEffect, useMemo, useState } from 'react';
import { FadeIn, SectionTitle, Card, Btn } from '../components/UI';
import {
  adminLogin,
  clearAdminSession,
  getAdminPages,
  getAdminSiteConfig,
  hasAdminSession,
  updateAdminPage,
  updateAdminSiteConfig,
} from '../services/api';
import { PAGE_CONTENT_FIELDS, PAGE_DEFAULTS, PAGE_ORDER } from '../data/pageDefaults';

function mergeWithDefaults(page) {
  const fallback = PAGE_DEFAULTS[page.pageKey] || {};
  return {
    ...fallback,
    ...page,
    content: {
      ...(fallback.content || {}),
      ...(page.content || {}),
    },
  };
}

export default function AdminPage({ onConfigUpdated }) {
  const [pages, setPages] = useState([]);
  const [authenticated, setAuthenticated] = useState(hasAdminSession());
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [siteSaving, setSiteSaving] = useState(false);
  const [gaTag, setGaTag] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!authenticated) {
      setLoading(false);
      return;
    }

    let mounted = true;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const [pageData, siteData] = await Promise.all([
          getAdminPages(),
          getAdminSiteConfig(),
        ]);

        if (!mounted) return;
        setPages(pageData.map(mergeWithDefaults));
        setGaTag(siteData.googleAnalyticsTag || '');
      } catch (err) {
        if (!mounted) return;
        setError(err.message || 'Failed to load admin data.');
        if ((err.message || '').toLowerCase().includes('unauthorized')) {
          clearAdminSession();
          setAuthenticated(false);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [authenticated]);

  const handleLogin = async () => {
    setError('');
    setNotice('');

    if (!password.trim()) {
      setError('Please enter the admin password.');
      return;
    }

    try {
      await adminLogin(password.trim());
      setAuthenticated(true);
      setPassword('');
      setNotice('Signed in.');
    } catch (err) {
      setError(err.message || 'Login failed.');
    }
  };

  const handleLogout = () => {
    clearAdminSession();
    setAuthenticated(false);
    setPages([]);
    setNotice('Signed out.');
  };

  const orderedPages = useMemo(() => {
    const byKey = Object.fromEntries(pages.map((p) => [p.pageKey, p]));
    const ordered = PAGE_ORDER.map((key) => byKey[key]).filter(Boolean);
    const remaining = pages.filter((p) => !PAGE_ORDER.includes(p.pageKey));
    return [...ordered, ...remaining];
  }, [pages]);

  const setField = (pageKey, field, value) => {
    setPages((prev) =>
      prev.map((page) =>
        page.pageKey === pageKey
          ? {
              ...page,
              content: {
                ...page.content,
                [field]: value,
              },
            }
          : page,
      ),
    );
  };

  const setEnabled = (pageKey, isEnabled) => {
    setPages((prev) =>
      prev.map((page) => (page.pageKey === pageKey ? { ...page, isEnabled } : page)),
    );
  };

  const savePage = async (page) => {
    setSavingKey(page.pageKey);
    setError('');
    setNotice('');
    try {
      const updated = await updateAdminPage(page.pageKey, {
        isEnabled: page.isEnabled,
        content: page.content,
      });

      setPages((prev) =>
        prev.map((item) => (item.pageKey === page.pageKey ? mergeWithDefaults(updated) : item)),
      );

      setNotice(`${page.displayName} updated.`);
      if (onConfigUpdated) {
        await onConfigUpdated();
      }
    } catch (err) {
      setError(err.message || 'Save failed.');
      if ((err.message || '').toLowerCase().includes('unauthorized')) {
        clearAdminSession();
        setAuthenticated(false);
      }
    } finally {
      setSavingKey('');
    }
  };

  const saveSiteConfig = async () => {
    setSiteSaving(true);
    setError('');
    setNotice('');

    try {
      const updated = await updateAdminSiteConfig({ googleAnalyticsTag: gaTag.trim() });
      setGaTag(updated.googleAnalyticsTag || '');
      setNotice('Google Analytics tag updated.');
    } catch (err) {
      setError(err.message || 'Save failed.');
      if ((err.message || '').toLowerCase().includes('unauthorized')) {
        clearAdminSession();
        setAuthenticated(false);
      }
    } finally {
      setSiteSaving(false);
    }
  };

  if (!authenticated) {
    return (
      <div style={{ paddingTop: 100, background: 'var(--dark)', minHeight: '100vh' }}>
        <section style={{ padding: '60px 40px 100px', maxWidth: 700, margin: '0 auto' }}>
          <FadeIn>
            <SectionTitle subtitle="Admin" title="Sign in" />
          </FadeIn>

          {error && (
            <p style={{ color: '#ef5350', fontFamily: 'var(--font-body)', marginBottom: 12 }}>{error}</p>
          )}

          <Card>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                fontFamily: 'var(--font-body)',
                color: 'var(--text-secondary)',
              }}
            >
              Admin password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleLogin();
                }}
                style={{
                  background: 'rgba(255,255,255,0.035)',
                  border: '1px solid var(--card-border)',
                  borderRadius: 8,
                  padding: '10px 12px',
                  fontFamily: 'var(--font-body)',
                  color: '#fff',
                }}
              />
            </label>

            <div style={{ marginTop: 14 }}>
              <Btn onClick={handleLogin}>Sign in</Btn>
            </div>
          </Card>
        </section>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ paddingTop: 100, background: 'var(--dark)', minHeight: '100vh' }}>
        <section style={{ padding: '60px 40px 100px', maxWidth: 1100, margin: '0 auto' }}>
          <SectionTitle subtitle="Admin" title="Content management" />
          <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }}>Loading...</p>
        </section>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 100, background: 'var(--dark)', minHeight: '100vh' }}>
      <section style={{ padding: '60px 40px 100px', maxWidth: 1100, margin: '0 auto' }}>
        <FadeIn>
          <SectionTitle subtitle="Admin" title="Page toggles & content" />
          <p
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.95rem',
              color: 'var(--text-secondary)',
              marginBottom: 24,
            }}
          >
            Toggle page visibility and update editable page text.
          </p>
        </FadeIn>

        {error && (
          <p style={{ color: '#ef5350', fontFamily: 'var(--font-body)', marginBottom: 12 }}>{error}</p>
        )}
        {notice && (
          <p style={{ color: 'var(--cyan)', fontFamily: 'var(--font-body)', marginBottom: 12 }}>{notice}</p>
        )}

        <Card style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <h3 style={{ marginTop: 0, fontFamily: 'var(--font-body)', color: 'var(--text-primary)' }}>
                Google Analytics
              </h3>
              <p style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', marginTop: 0 }}>
                Enter your Measurement ID (for example: G-XXXXXXXXXX). This is injected across all pages.
              </p>
              <input
                value={gaTag}
                onChange={(e) => setGaTag(e.target.value)}
                placeholder="G-XXXXXXXXXX"
                style={{
                  width: '100%',
                  background: 'rgba(255,255,255,0.035)',
                  border: '1px solid var(--card-border)',
                  borderRadius: 8,
                  padding: '10px 12px',
                  fontFamily: 'var(--font-body)',
                  color: '#fff',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ marginTop: 12, display: 'flex', gap: 10 }}>
                <Btn onClick={saveSiteConfig}>{siteSaving ? 'Saving...' : 'Save GA Tag'}</Btn>
                <Btn outline onClick={handleLogout}>Sign out</Btn>
              </div>
            </div>
          </div>
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {orderedPages.map((page, index) => {
            const fields = PAGE_CONTENT_FIELDS[page.pageKey] || [];
            return (
              <FadeIn key={page.pageKey} delay={index * 0.05}>
                <Card>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 14,
                      alignItems: 'center',
                      marginBottom: 18,
                      flexWrap: 'wrap',
                    }}
                  >
                    <div>
                      <h3
                        style={{
                          margin: 0,
                          fontFamily: 'var(--font-body)',
                          fontSize: '1.05rem',
                          color: 'var(--text-primary)',
                        }}
                      >
                        {page.displayName}
                      </h3>
                      <p
                        style={{
                          margin: '4px 0 0',
                          fontFamily: 'var(--font-mono)',
                          fontSize: '0.72rem',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {page.path}
                      </p>
                    </div>

                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontFamily: 'var(--font-body)',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(page.isEnabled)}
                        onChange={(e) => setEnabled(page.pageKey, e.target.checked)}
                      />
                      Enabled
                    </label>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                    {fields.map((field) => {
                      const value = page.content?.[field.key] ?? '';
                      return (
                        <label
                          key={field.key}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                            fontFamily: 'var(--font-body)',
                            fontSize: '0.82rem',
                            color: 'var(--text-muted)',
                          }}
                        >
                          {field.label}
                          {field.multiline ? (
                            <textarea
                              value={value}
                              onChange={(e) => setField(page.pageKey, field.key, e.target.value)}
                              style={{
                                background: 'rgba(255,255,255,0.035)',
                                border: '1px solid var(--card-border)',
                                borderRadius: 8,
                                padding: '10px 12px',
                                fontFamily: 'var(--font-body)',
                                color: '#fff',
                                minHeight: 90,
                              }}
                            />
                          ) : (
                            <input
                              value={value}
                              onChange={(e) => setField(page.pageKey, field.key, e.target.value)}
                              style={{
                                background: 'rgba(255,255,255,0.035)',
                                border: '1px solid var(--card-border)',
                                borderRadius: 8,
                                padding: '10px 12px',
                                fontFamily: 'var(--font-body)',
                                color: '#fff',
                              }}
                            />
                          )}
                        </label>
                      );
                    })}
                  </div>

                  <div style={{ marginTop: 14 }}>
                    <Btn onClick={() => savePage(page)}>
                      {savingKey === page.pageKey ? 'Saving...' : 'Save changes'}
                    </Btn>
                  </div>
                </Card>
              </FadeIn>
            );
          })}
        </div>
      </section>
    </div>
  );
}
